// frontend/src/pages/OrganizationsPage.tsx - REFACTORED WITH STANDALONE MODAL
// Complete Organizations page with separated modal component

import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, truncateText, formatFileSize } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// Import hooks
import { 
  useOrganizations,
  useOrganization,
  useCreateOrganization,
  useUpdateOrganization,
  useDeleteOrganization,
  useActivateOrganization,
  useDeactivateOrganization
} from '@/hooks/useOrganizations';

// Import standalone modal component
import { OrganizationModal } from '@/components/organizations/OrganizationModal';

// Import types
import type {
  Organization,
  OrganizationQueryParams,
  OrganizationCreateRequest,
  OrganizationUpdateRequest,
  UserRole,
  NormalizedUser
} from '@/api/types';

// ===== UTILITY COMPONENTS =====

const SubscriptionBadge: React.FC<{ plan?: string }> = memo(({ plan = 'basic' }) => {
  const planConfig = {
    basic: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300', label: 'Basic' },
    standard: { color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300', label: 'Standard' },
    enterprise: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300', label: 'Enterprise' }
  };

  const config = planConfig[plan as keyof typeof planConfig] || planConfig.basic;
  
  return (
    <span className={cn('px-2 py-1 text-xs font-medium rounded-full', config.color)}>
      {config.label}
    </span>
  );
});

const StatusBadge: React.FC<{ isActive: boolean }> = memo(({ isActive }) => {
  return (
    <span className={cn(
      'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
      isActive
        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
    )}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
});

const OrganizationCard: React.FC<{
  organization: Organization;
  currentUser: NormalizedUser | null;
  onEdit: (org: Organization) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string, isActive: boolean) => void;
  isOwn?: boolean;
}> = memo(({ organization, currentUser, onEdit, onDelete, onToggleStatus, isOwn = false }) => {
  const navigate = useNavigate();
  const canEdit = currentUser?.role === 'implementor' || (currentUser?.role === 'admin' && isOwn);
  const canDelete = currentUser?.role === 'implementor' && organization.deactivated === true; // Can only delete deactivated organizations
  const canToggleStatus = currentUser?.role === 'implementor';

  // Navigation handlers for clickable counts
  const handleNavigateToUsers = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const orgId = organization.id || organization._id || '';
    navigate(`/users?org=${orgId}`);
  }, [navigate, organization]);

  const handleNavigateToGroups = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const orgId = organization.id || organization._id || '';
    navigate(`/groups?org=${orgId}`);
  }, [navigate, organization]);

  const handleNavigateToProjects = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const orgId = organization.id || organization._id || '';
    navigate(`/projects?org=${orgId}`);
  }, [navigate, organization]);

  const handleNavigateToWorkflows = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const orgId = organization.id || organization._id || '';
    navigate(`/workflows?organization=${orgId}`);
  }, [navigate, organization]);

  // Calculate warning indicators
  const warnings = [];
  const userPercentage = organization.maxUsers ? ((organization as any).userCount / organization.maxUsers) * 100 : 0;
  const storagePercentage = (organization as any).maxTotalStorage ?
    (((organization as any).totalStorageUsed || 0) / (organization as any).maxTotalStorage) * 100 : 0;

  if (userPercentage >= 90) warnings.push({ type: 'users', severity: 'critical' });
  else if (userPercentage >= 80) warnings.push({ type: 'users', severity: 'warning' });

  if (storagePercentage >= 90) warnings.push({ type: 'storage', severity: 'critical' });
  else if (storagePercentage >= 80) warnings.push({ type: 'storage', severity: 'warning' });

  return (
    <div className={cn(
      "bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow",
      warnings.some(w => w.severity === 'critical')
        ? "border-red-300 dark:border-red-800"
        : warnings.length > 0
        ? "border-orange-300 dark:border-orange-800"
        : "border-gray-200 dark:border-gray-700"
    )}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {organization.name}
            </h3>
            {isOwn && (
              <span className="text-xs bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 px-2 py-1 rounded-full">
                Your Organization
              </span>
            )}
            {warnings.length > 0 && (
              <span className={cn(
                "text-xs px-2 py-1 rounded-full font-medium",
                warnings.some(w => w.severity === 'critical')
                  ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300"
                  : "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300"
              )}>
                ⚠️ {warnings.some(w => w.severity === 'critical') ? 'At Limit' : 'Near Limit'}
              </span>
            )}
          </div>
          {organization.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
              {truncateText(organization.description, 100)}
            </p>
          )}
        </div>

        <div className="flex items-center space-x-2 ml-4">
          <StatusBadge isActive={organization.isActive !== false} />
          <SubscriptionBadge plan={organization.plan} />
        </div>
      </div>

      {/* Stats Grid - Enhanced with more metrics and clickable navigation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
        <button
          onClick={handleNavigateToUsers}
          className="text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
        >
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Users</div>
          <div className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
            {organization.userCount || 0}
            {organization.maxUsers && (
              <span className="text-gray-500 dark:text-gray-400 font-normal"> / {organization.maxUsers}</span>
            )}
          </div>
        </button>

        <button
          onClick={handleNavigateToGroups}
          className="text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
        >
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Groups</div>
          <div className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
            {(organization as any).groupCount || 0}
          </div>
        </button>

        <button
          onClick={handleNavigateToProjects}
          className="text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
        >
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Projects</div>
          <div className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
            {organization.projectCount || (organization as any).numProjects || 0}
            {organization.maxProjects && (
              <span className="text-gray-500 dark:text-gray-400 font-normal"> / {organization.maxProjects}</span>
            )}
          </div>
        </button>

        <button
          onClick={handleNavigateToWorkflows}
          className="text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
        >
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Workflows</div>
          <div className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
            {(organization as any).workflowCount || 0}
          </div>
        </button>

        <div className="col-span-2">
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Storage Used</div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-gray-900 dark:text-white">
                {formatFileSize((organization as any).totalStorageUsed || 0)}
              </span>
              {(organization as any).maxTotalStorage && (
                <span className="text-gray-500 dark:text-gray-400 text-xs">
                  of {formatFileSize((organization as any).maxTotalStorage)}
                </span>
              )}
            </div>
            {/* Progress Bar */}
            {(organization as any).maxTotalStorage && (
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={cn(
                    "h-2 rounded-full transition-all",
                    (() => {
                      const percentage = (((organization as any).totalStorageUsed || 0) / (organization as any).maxTotalStorage) * 100;
                      if (percentage >= 90) return "bg-red-600";
                      if (percentage >= 80) return "bg-orange-500";
                      return "bg-blue-600";
                    })()
                  )}
                  style={{
                    width: `${Math.min(100, (((organization as any).totalStorageUsed || 0) / ((organization as any).maxTotalStorage || 1)) * 100)}%`
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Emails</div>
          <div className="font-semibold text-gray-900 dark:text-white">
            {organization.emailCount || (organization as any).numEmails || 0}
          </div>
        </div>

        <div>
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Correspondences</div>
          <div className="font-semibold text-gray-900 dark:text-white">
            {(organization as any).correspondenceCount || 0}
          </div>
        </div>

        <div>
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">AI Enabled</div>
          <div className="font-semibold text-gray-900 dark:text-white">
            {organization.isAIEnabled ? '✓ Yes' : '✗ No'}
          </div>
        </div>
      </div>

      {/* Subscribed Modules */}
      {organization.subscribedModules && organization.subscribedModules.length > 0 && (
        <div className="mb-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">Modules:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {organization.subscribedModules.slice(0, 3).map((module, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded"
              >
                {module}
              </span>
            ))}
            {organization.subscribedModules.length > 3 && (
              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 rounded">
                +{organization.subscribedModules.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
          <span>Created: {formatDate(organization.createdAt)}</span>
        </div>
        
        <div className="flex items-center space-x-2">
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(organization)}
            >
              Edit
            </Button>
          )}
          
          {canToggleStatus && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggleStatus(
                organization.id || organization._id || '', 
                !(organization.isActive !== false)
              )}
            >
              {organization.isActive !== false ? 'Deactivate' : 'Activate'}
            </Button>
          )}
          
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(organization.id || organization._id || '')}
              className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
              title="Delete this organization (requires: deactivated status + no active users)"
            >
              Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

// ===== ERROR BOUNDARY =====

class OrganizationsErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('OrganizationsPage Error Boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Organizations Error</h2>
          <p className="text-gray-600 mb-4">Something went wrong loading the organizations page.</p>
          <Button onClick={() => this.setState({ hasError: false })}>
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== UTILITY HOOKS =====

// Debounced value hook
function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ===== MAIN COMPONENT =====

export const OrganizationsPage: React.FC = () => {
  // ===== HOOKS =====

  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuthStore();
  const { setGlobalError, clearGlobalError, addNotification } = useAppStore();
  const { confirm } = useConfirmation();
  const [searchParams] = useSearchParams();

  // ===== LOCAL STATE =====

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'deactivated'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'users' | 'storage'>('name');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<Organization | undefined>();

  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  // Get organization ID from URL if present
  const urlOrgId = searchParams.get('org');

  // ===== COMPUTED VALUES =====

  // Check permissions
  const permissions = useMemo(() => {
    if (!currentUser) return { canView: false, canCreate: false, canEdit: false, canDelete: false };

    switch (currentUser.role) {
      case 'implementor':
        return { canView: true, canCreate: true, canEdit: true, canDelete: true };
      case 'admin':
        return { canView: true, canCreate: false, canEdit: true, canDelete: false }; // Can edit own org
      default:
        return { canView: true, canCreate: false, canEdit: false, canDelete: false };
    }
  }, [currentUser?.role]);

  // Build query params with debounced search
  const queryParams = useMemo((): OrganizationQueryParams => {
    return {
      search: debouncedSearchTerm || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      limit: 50 // Reasonable limit for organizations
    };
  }, [debouncedSearchTerm, statusFilter]);

  // ===== API HOOKS =====

  const {
    data: organizationsData,
    loading,
    error,
    refetch: refreshOrganizations
  } = useOrganizations(queryParams, {
    immediate: permissions.canView
  });


  const {
    mutate: createOrganization,
    loading: createLoading,
    error: createError
  } = useCreateOrganization({
    onSuccess: () => {
      setIsModalOpen(false);
      setEditingOrganization(undefined);
      refreshOrganizations();
      clearGlobalError();
    },
    onError: (error) => {
      setGlobalError(error.message || 'Failed to create organization');
    }
  });

  const {
    mutate: updateOrganization,
    loading: updateLoading,
    error: updateError
  } = useUpdateOrganization({
    onSuccess: () => {
      setIsModalOpen(false);
      setEditingOrganization(undefined);
      refreshOrganizations();
      clearGlobalError();
    },
    onError: (error) => {
      setGlobalError(error.message || 'Failed to update organization');
    }
  });

  const {
    mutate: deleteOrganization,
    loading: deleteLoading,
    error: deleteError
  } = useDeleteOrganization({
    onSuccess: () => {
      refreshOrganizations();
      clearGlobalError();
    }
    // Note: onError intentionally omitted - error handling is done in handleDeleteOrganization
    // This prevents duplicate error messages
  });

  const {
    mutate: activateOrganization,
    loading: activateLoading,
    error: activateError
  } = useActivateOrganization({
    onSuccess: () => {
      refreshOrganizations();
      clearGlobalError();
    },
    onError: (error) => {
      setGlobalError(error.message || 'Failed to activate organization');
    }
  });

  const {
    mutate: deactivateOrganization,
    loading: deactivateLoading,
    error: deactivateError
  } = useDeactivateOrganization({
    onSuccess: () => {
      refreshOrganizations();
      clearGlobalError();
    },
    onError: (error) => {
      setGlobalError(error.message || 'Failed to deactivate organization');
    }
  });

  // ===== EFFECTS =====

  // Clear errors when component mounts
  useEffect(() => {
    return () => clearGlobalError();
  }, [clearGlobalError]);

  // ===== COMPUTED VALUES =====

  const organizations = organizationsData?.data || [];
  const currentUserOrgId = useMemo(() => {
    if (!currentUser?.organization) return undefined;
    if (typeof currentUser.organization === 'string') {
      return currentUser.organization;
    }
    // Handle organization object with proper type checking
    const orgObj = currentUser.organization as any;
    return orgObj._id || orgObj.id;
  }, [currentUser?.organization]);

  // Filter and sort organizations based on user role
  const filteredOrganizations = useMemo(() => {
    let filtered = organizations;

    // Filter by specific org ID from URL parameter (only for implementors)
    if (urlOrgId && currentUser?.role === 'implementor') {
      filtered = organizations.filter(org =>
        ((org as any)._id === urlOrgId || (org as any).id === urlOrgId)
      );
    }
    // Filter by role
    else if (currentUser?.role !== 'implementor') {
      // Non-implementors can only see their own organization
      if (currentUser?.organization) {
        const userOrgId = currentUserOrgId;
        filtered = organizations.filter(org => ((org as any)._id || (org as any).id) === userOrgId);
      } else {
        filtered = [];
      }
    }

    // Sort organizations
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'created':
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        case 'users':
          return ((b as any).userCount || 0) - ((a as any).userCount || 0);
        case 'storage':
          return ((b as any).totalStorageUsed || 0) - ((a as any).totalStorageUsed || 0);
        default:
          return 0;
      }
    });

    return sorted;
  }, [organizations, currentUser, currentUserOrgId, sortBy, urlOrgId]);

  // ===== EVENT HANDLERS =====

  const handleCreateOrganization = useCallback(() => {
    setEditingOrganization(undefined);
    setIsModalOpen(true);
  }, []);

  const handleEditOrganization = useCallback((organization: Organization) => {
    setEditingOrganization(organization);
    setIsModalOpen(true);
  }, []);

  const handleDeleteOrganization = useCallback(async (organizationId: string) => {
    const confirmed = await confirm({
      title: 'Delete Organization',
      message: 'Are you sure you want to delete this organization? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }

    try {
      await deleteOrganization(organizationId);

      // Show success notification
      addNotification({
        type: 'success',
        title: 'Organization Deleted',
        message: 'The organization has been successfully deleted.',
        priority: 'medium'
      });
    } catch (error: any) {
      // Extract error message from the API response
      const errorMessage = error?.response?.data?.message ||
                          error?.response?.data?.error ||
                          error?.message ||
                          'Failed to delete organization';

      // Get additional details if available
      const dependencies = error?.response?.data?.dependencies;
      let detailedMessage = errorMessage;

      if (dependencies?.activeUsers) {
        detailedMessage = `Organization has ${dependencies.activeUsers} active user(s). Please deactivate or transfer all users before deleting the organization.`;
      }

      // Show error notification
      addNotification({
        type: 'error',
        title: 'Cannot Delete Organization',
        message: detailedMessage,
        priority: 'high',
        persistent: true
      });
    }
  }, [deleteOrganization, addNotification, confirm]);

  const handleToggleStatus = useCallback(async (organizationId: string, shouldActivate: boolean) => {
    const action = shouldActivate ? 'activate' : 'deactivate';
    const confirmed = await confirm({
      title: `${shouldActivate ? 'Activate' : 'Deactivate'} Organization`,
      message: `Are you sure you want to ${action} this organization?`,
      confirmText: shouldActivate ? 'Activate' : 'Deactivate',
      variant: 'warning',
    });
    if (!confirmed) {
      return;
    }

    try {
      if (shouldActivate) {
        await activateOrganization(organizationId);
      } else {
        await deactivateOrganization(organizationId);
      }
    } catch (error) {
      console.error(`${action} organization error:`, error);
      // Error is already handled by the hook's onError callback
    }
  }, [activateOrganization, deactivateOrganization, confirm]);

  const handleSaveOrganization = useCallback(async (data: OrganizationCreateRequest | OrganizationUpdateRequest) => {
    try {
      if (editingOrganization) {
        // Update existing organization
        const orgId = editingOrganization.id || editingOrganization._id || '';
        await updateOrganization({ id: orgId, data: data as OrganizationUpdateRequest });
      } else {
        // Create new organization
        await createOrganization(data as OrganizationCreateRequest);
      }
    } catch (error) {
      console.error('Failed to save organization:', error);
    }
  }, [editingOrganization, updateOrganization, createOrganization]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingOrganization(undefined);
  }, []);

  // ===== RENDER HELPERS =====

  const renderEmptyState = () => (
    <div className="text-center py-12">
      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h2M7 3h10a2 2 0 012 2v4M7 3H5a2 2 0 00-2 2v4m0 0v6m0 0v4a2 2 0 002 2h2m-6-4h2m2-6h2" />
      </svg>
      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No organizations found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {searchTerm 
          ? 'No organizations match your search criteria.' 
          : 'Get started by creating your first organization.'
        }
      </p>
      {permissions.canCreate && !searchTerm && (
        <div className="mt-6">
          <Button onClick={handleCreateOrganization}>
            Create Organization
          </Button>
        </div>
      )}
    </div>
  );

  const renderError = () => {
    const errorMessage = error || createError || updateError || deleteError || activateError || deactivateError || 'An error occurred';
    
    return (
      <ErrorAlert 
        message={errorMessage}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={refreshOrganizations}
            className="mt-2"
          >
            Try Again
          </Button>
        }
      />
    );
  };

  const renderLoading = () => (
    <div className="flex items-center justify-center py-12">
      <LoadingSpinner size="lg" />
      <span className="ml-3 text-gray-600 dark:text-gray-400">Loading organizations...</span>
    </div>
  );

  // ===== MAIN RENDER =====

  if (!isAuthenticated || !permissions.canView) {
    return (
      <PageLayout title="Organizations" subtitle="Access denied">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Access Denied</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            You don't have permission to view organizations.
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <OrganizationsErrorBoundary>
      <PageLayout 
        title="Organizations" 
        subtitle={`Manage organizations and their settings${currentUser?.role === 'implementor' ? '' : ' for your organization'}`}
      >
        <div className="space-y-6">
          {/* Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* Search */}
              <div className="flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder="Search organizations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Status Filter */}
              <div className="w-full sm:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="deactivated">Inactive</option>
                </select>
              </div>

              {/* Sort By */}
              <div className="w-full sm:w-auto">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'created' | 'users' | 'storage')}
                  className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="name">Sort: Name (A-Z)</option>
                  <option value="created">Sort: Newest First</option>
                  <option value="users">Sort: Most Users</option>
                  <option value="storage">Sort: Most Storage</option>
                </select>
              </div>

              {/* Clear Organization Filter */}
              {urlOrgId && currentUser?.role === 'implementor' && (
                <div className="w-full sm:w-auto">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/organizations')}
                    className="w-full"
                  >
                    Show All Organizations
                  </Button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={refreshOrganizations}
                disabled={loading}
              >
                Refresh
              </Button>
              
              {permissions.canCreate && (
                <Button
                  onClick={handleCreateOrganization}
                  disabled={loading}
                >
                  Create Organization
                </Button>
              )}
            </div>
          </div>

          {/* Content */}
          {error ? renderError() : loading ? renderLoading() : filteredOrganizations.length === 0 ? renderEmptyState() : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredOrganizations.map((organization) => (
                <OrganizationCard
                  key={organization.id || organization._id}
                  organization={organization}
                  currentUser={currentUser}
                  onEdit={handleEditOrganization}
                  onDelete={handleDeleteOrganization}
                  onToggleStatus={handleToggleStatus}
                  isOwn={currentUserOrgId === (organization.id || organization._id)}
                />
              ))}
            </div>
          )}

          {/* Organization Modal */}
          <OrganizationModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            organization={editingOrganization}
            onSave={handleSaveOrganization}
            loading={createLoading || updateLoading}
          />
        </div>
      </PageLayout>
    </OrganizationsErrorBoundary>
  );
};